import { ELTagButton } from '@davincihealthcare/elty-design-system-react';
import { RefObject, useRef, useState } from 'react';
import { useOutsideClick } from '@/app/_hooks/useOutsideClick';
import { FilterStates, useListingFilters } from '@/app/_hooks/useListingFilters';
import SelectRadioButton from './selectRadioButton';
import { CalendarIcon, CreditCardIcon, HomeIcon } from '@heroicons/react/24/outline';
import { dateOptions, locationOptions, paymentOptions } from '@/app/_components/widgets/listing/listingFilters/utils';
import SelectCheckBox from '@/app/_components/widgets/listing/listingFilters/selectCheckbox';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { useIsMobile } from '@/app/_hooks/useIsScreen';

type FilterButtonProps<T> = {
  filterLight?: boolean;
};

const FilterButtons = <T extends keyof FilterStates>({}: FilterButtonProps<T>) => {
  const { filters, applyFilter } = useListingFilters();
  const [modalFilters, setModalFilters] = useState(filters);
  const isMobileAndSmaller = useIsMobile({ andSmaller: true });

  const isWelfare = useIsWelfare();

  const getFilterCallback =
    <T extends keyof FilterStates>(filter: T) =>
    (newValue: FilterStates[T]) => {
      setModalFilters(currentVal => ({
        ...currentVal,
        [filter]: newValue,
      }));
    };

  const [openModal, setOpenModal] = useState<keyof Partial<FilterStates>>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const dateAnchorRef = useRef<HTMLDivElement>(null);
  const paymentAnchorRef = useRef<HTMLDivElement>(null);
  const channelAnchorRef = useRef<HTMLDivElement>(null);
  useOutsideClick([wrapperRef, popoverRef] as RefObject<HTMLDivElement>[], () => setOpenModal(undefined));

  return (
    <div className="block overflow-x-auto bg-primary-light-active px-4 xl:px-20">
      <div className="grid h-14 grid-rows-1 items-center">
        <div className="flex items-center gap-x-3" data-cy="search-result-filter-bar" ref={wrapperRef}>
          <span className="hidden text-nowrap text-sm font-medium text-neutral-darker sm:block">Perfeziona la tua ricerca</span>

          <div className="relative text-nowrap" ref={dateAnchorRef}>
            <ELTagButton
              label="Date disponibili"
              leadingIcon={isMobileAndSmaller ? undefined : <CalendarIcon />}
              state={(openModal === 'dateFilter' && 'focused') || (filters.dateFilter && 'active') || undefined}
              onClick={() => {
                if (openModal === 'dateFilter') {
                  setOpenModal(undefined);
                } else {
                  setOpenModal('dateFilter');
                }
              }}
            />

            {openModal === 'dateFilter' && (
              <SelectRadioButton
                anchorRef={dateAnchorRef}
                popoverRef={popoverRef}
                label={dateOptions.title}
                options={[...dateOptions.options, { label: dateOptions.defaultOptionLabel, value: undefined }]}
                selected={modalFilters.dateFilter}
                setSelected={getFilterCallback('dateFilter')}
                primaryCTA={{
                  label: 'Applica',
                  onClick: () => {
                    applyFilter('dateFilter', modalFilters.dateFilter);

                    setOpenModal(undefined);
                  },
                }}
                secondaryCTA={{
                  label: 'Annulla questo filtro',
                  onClick: () => {
                    getFilterCallback('dateFilter')(undefined);
                    applyFilter('dateFilter', undefined);
                    setOpenModal(undefined);
                  },
                }}
                floating
              />
            )}
          </div>
          {!isWelfare && (
            <div className="relative text-nowrap" ref={paymentAnchorRef}>
              <ELTagButton
                label="Pagamento"
                leadingIcon={isMobileAndSmaller ? undefined : <CreditCardIcon />}
                state={(openModal === 'paymentFilter' && 'focused') || (filters.paymentFilter && 'active') || undefined}
                onClick={() => {
                  if (openModal === 'paymentFilter') {
                    setOpenModal(undefined);
                  } else {
                    setOpenModal('paymentFilter');
                  }
                }}
              />
              {openModal === 'paymentFilter' && (
                <SelectRadioButton
                  anchorRef={paymentAnchorRef}
                  popoverRef={popoverRef}
                  label={paymentOptions.title}
                  options={[...paymentOptions.options, { label: paymentOptions.defaultOptionLabel, value: undefined }]}
                  selected={modalFilters.paymentFilter}
                  setSelected={getFilterCallback('paymentFilter')}
                  primaryCTA={{
                    label: 'Applica',
                    onClick: () => {
                      applyFilter('paymentFilter', modalFilters.paymentFilter);
                      setOpenModal(undefined);
                    },
                  }}
                  secondaryCTA={{
                    label: 'Annulla questo filtro',
                    onClick: () => {
                      getFilterCallback('paymentFilter')(undefined);
                      applyFilter('paymentFilter', undefined);
                      setOpenModal(undefined);
                    },
                  }}
                  floating
                />
              )}
            </div>
          )}
          <div className="relative text-nowrap" ref={channelAnchorRef}>
            <ELTagButton
              label="Domicilio o in sede"
              leadingIcon={isMobileAndSmaller ? undefined : <HomeIcon />}
              state={(openModal === 'channelFilter' && 'focused') || (filters.channelFilter?.length && 'active') || undefined}
              onClick={() => {
                if (openModal === 'channelFilter') {
                  setOpenModal(undefined);
                } else {
                  setOpenModal('channelFilter');
                }
              }}
            />
            {openModal === 'channelFilter' && (
              <SelectCheckBox
                anchorRef={channelAnchorRef}
                popoverRef={popoverRef}
                label={locationOptions.title}
                options={[...locationOptions.options, { label: locationOptions.defaultOptionLabel, value: undefined }]}
                selected={modalFilters.channelFilter}
                setSelected={getFilterCallback('channelFilter')}
                primaryCTA={{
                  label: 'Applica',
                  onClick: () => {
                    applyFilter('channelFilter', modalFilters.channelFilter);
                    setOpenModal(undefined);
                  },
                }}
                secondaryCTA={{
                  label: 'Annulla questo filtro',
                  onClick: () => {
                    getFilterCallback('channelFilter')(undefined);
                    applyFilter('channelFilter', []);
                    setOpenModal(undefined);
                  },
                }}
                floating
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterButtons;
